import React, { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import { useDraggablePanel } from '../utils/panelDragUtils';
import { OverlayPanel } from '../ui/OverlayPanel';
import {
  SkillName,
  SkillCategory,
  PlayerSkills,
  SkillXPGain,
  SkillLevelUp,
  SKILL_INFO,
  SKILLS_BY_CATEGORY,
  CATEGORY_COLORS,
  CATEGORY_NAMES,
  xpToNextLevel,
  createDefaultPlayerSkills,
} from '../game/types';

// Inject shimmer animation styles
const injectShimmerStyles = (): void => {
  const styleId = 'skills-shimmer-style';
  if (document.getElementById(styleId)) return;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes skillsShimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    @keyframes skillLevelUp {
      0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 215, 0, 0.7); }
      50% { transform: scale(1.05); box-shadow: 0 0 20px 10px rgba(255, 215, 0, 0.3); }
      100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 215, 0, 0); }
    }
    @keyframes xpFloat {
      0% { opacity: 1; transform: translateY(0); }
      100% { opacity: 0; transform: translateY(-20px); }
    }
  `;
  document.head.appendChild(style);
};

interface SkillsPanelProps {
  socket: Socket | null;
  isOpen: boolean;
  onClose: () => void;
}

interface XPNotification {
  id: number;
  skill: SkillName;
  amount: number;
  timestamp: number;
}

// Skill guide data - resources unlocked at each level (RuneScape-style staggered levels)
interface SkillGuideEntry {
  level: number;
  name: string;
  description: string;
  type: 'resource' | 'ability' | 'bonus';
}

const SKILL_GUIDES: Partial<Record<SkillName, SkillGuideEntry[]>> = {
  salvaging: [
    { level: 1, name: 'Shipwreck Debris', description: 'Basic salvage from sunken vessels', type: 'resource' },
    { level: 10, name: 'Sunken Rowboat', description: 'Small capsized boats with salvageable parts', type: 'resource' },
    { level: 20, name: 'Merchant Wreck', description: 'Trading vessel remains with trade goods', type: 'resource' },
    { level: 25, name: 'Abandoned Cargo', description: 'Floating merchant crates', type: 'resource' },
    { level: 30, name: 'Naval Wreck', description: 'Military vessel wreckage with naval supplies', type: 'resource' },
    { level: 40, name: 'Pirate Cache', description: 'Hidden treasure from legendary pirates', type: 'resource' },
    { level: 50, name: 'Ancient Ruins', description: 'Lost civilization structures', type: 'resource' },
    { level: 60, name: 'Sunken Temple', description: 'Ancient artifacts and enchanted items', type: 'resource' },
    { level: 70, name: 'Ghost Ship', description: 'Cursed items and spectral materials', type: 'resource' },
    { level: 80, name: 'Titan Remains', description: 'Massive creature bones and legendary materials', type: 'resource' },
    { level: 90, name: 'Void Rift Debris', description: 'Otherworldly materials from the void', type: 'resource' },
    { level: 99, name: 'Lost Vault', description: 'Ultimate treasures of lost civilizations', type: 'resource' },
  ],
  mining: [
    { level: 1, name: 'Stone Quarry', description: 'Copper and iron ore fragments', type: 'resource' },
    { level: 10, name: 'Tin Deposit', description: 'Tin ore for bronze crafting', type: 'resource' },
    { level: 15, name: 'Mineral Outcrop', description: 'Mixed ore veins with rare metals', type: 'resource' },
    { level: 20, name: 'Silver Vein', description: 'Glittering silver ore deposits', type: 'resource' },
    { level: 25, name: 'Gold Deposit', description: 'Rich gold ore veins', type: 'resource' },
    { level: 30, name: 'Coal Seam', description: 'Coal for steel production', type: 'resource' },
    { level: 40, name: 'Volcanic Vent', description: 'Rare minerals and obsidian', type: 'resource' },
    { level: 50, name: 'Mythril Deposit', description: 'Legendary metal ore', type: 'resource' },
    { level: 60, name: 'Adamant Vein', description: 'Elite equipment materials', type: 'resource' },
    { level: 70, name: 'Runic Deposit', description: 'Magical metal ore', type: 'resource' },
    { level: 80, name: 'Dragon Ore', description: 'Ultimate metal for legendary gear', type: 'resource' },
    { level: 90, name: 'Void Crystal', description: 'Otherworldly crystalline material', type: 'resource' },
    { level: 99, name: 'Primordial Shard', description: 'The rarest ore in existence', type: 'resource' },
  ],
  foraging: [
    { level: 1, name: 'Driftwood Cluster', description: 'Floating logs and timber', type: 'resource' },
    { level: 5, name: 'Coconut Palm', description: 'Tropical palms with coconuts', type: 'resource' },
    { level: 10, name: 'Seaweed Bed', description: 'Dense marine vegetation', type: 'resource' },
    { level: 15, name: 'Herb Patch', description: 'Wild herbs for alchemy', type: 'resource' },
    { level: 20, name: 'Fruit Grove', description: 'Fruit-bearing trees', type: 'resource' },
    { level: 25, name: 'Mushroom Colony', description: 'Various mushrooms for potions', type: 'resource' },
    { level: 30, name: 'Rare Flower Bed', description: 'Rare flowers with alchemical properties', type: 'resource' },
    { level: 40, name: 'Ancient Tree', description: 'Hardwood and magical sap', type: 'resource' },
    { level: 50, name: 'Crystal Garden', description: 'Magical plants with crystalline properties', type: 'resource' },
    { level: 60, name: 'Bioluminescent Grove', description: 'Glowing materials and rare flora', type: 'resource' },
    { level: 70, name: 'Elder Growth', description: 'Legendary wood and ancient plants', type: 'resource' },
    { level: 80, name: 'World Tree Sapling', description: 'Mythical materials from the world tree', type: 'resource' },
    { level: 90, name: 'Void Bloom', description: 'Otherworldly flora from the void', type: 'resource' },
    { level: 99, name: 'World Root Fragment', description: 'Ultimate plant material from legend', type: 'resource' },
  ],
   fishing: [
     { level: 1, name: 'Shallow Waters', description: 'Common coastal fish', type: 'resource' },
     { level: 1, name: 'Deep Sea', description: 'Rare deep ocean species', type: 'resource' },
     { level: 10, name: 'Coral Reef', description: 'Shellfish and reef species', type: 'resource' },
     { level: 20, name: 'Fishing Hole', description: 'Concentrated spawns of valuable fish', type: 'resource' },
     { level: 30, name: 'Shipwreck Pool', description: 'Dangerous predators near wrecks', type: 'resource' },
     { level: 40, name: 'Volcanic Waters', description: 'Heat-resistant exotic species', type: 'resource' },
     { level: 50, name: 'Abyssal Trench', description: 'Legendary deep-sea creatures', type: 'resource' },
     { level: 60, name: 'Whirlpool Edge', description: 'High-risk/high-reward dangerous currents', type: 'resource' },
     { level: 70, name: 'Moonlit Shallows', description: 'Night-only celestial fish spawns', type: 'resource' },
     { level: 80, name: 'Ancient Grounds', description: 'Mythical fishing location with elder species', type: 'resource' },
   ],
   shipwright: [
     { level: 1, name: 'Iron Cannons', description: 'Basic ship armament crafting', type: 'ability' },
     { level: 5, name: 'Powder Magazine', description: 'Ammunition storage modules', type: 'ability' },
     { level: 10, name: 'Basic Sights', description: 'Targeting enhancement', type: 'ability' },
     { level: 15, name: 'Bronze Cannons', description: 'Improved firepower modules', type: 'ability' },
     { level: 20, name: 'Wooden Hull', description: 'Ship hull reinforcement', type: 'ability' },
     { level: 30, name: 'Steel Cannons', description: 'Advanced weaponry', type: 'ability' },
     { level: 40, name: 'Plating Armor', description: 'Heavy armor plating', type: 'ability' },
     { level: 50, name: 'Advanced Rigging', description: 'Superior sail configuration', type: 'ability' },
     { level: 60, name: 'Mythril Hull', description: 'Legendary hull reinforcement', type: 'ability' },
     { level: 75, name: 'Grand Armament', description: 'Ultimate weapon systems', type: 'ability' },
     { level: 90, name: 'Void Plating', description: 'Otherworldly armor materials', type: 'ability' },
     { level: 99, name: 'Perfect Vessel', description: 'The ultimate ship configuration', type: 'ability' },
   ],
   cooking: [
     { level: 1, name: 'Basic Meal', description: 'Simple hunger relief', type: 'ability' },
     { level: 5, name: 'Herb Stew', description: 'Mild healing effects', type: 'ability' },
     { level: 10, name: 'Fish Dinner', description: 'Nutritious seafood meals', type: 'ability' },
     { level: 15, name: 'Exotic Curry', description: 'Spiced dishes for endurance', type: 'ability' },
     { level: 20, name: 'Royal Feast', description: 'Premium nutrition', type: 'ability' },
     { level: 30, name: 'Dragon Steak', description: 'Legendary meat dishes', type: 'ability' },
     { level: 40, name: 'Celestial Broth', description: 'Magical food preparation', type: 'ability' },
      { level: 50, name: 'Philosopher\'s Meal', description: 'Wisdom-enhancing food', type: 'ability' },
      { level: 65, name: 'Elder\'s Feast', description: 'Ancient recipe mastery', type: 'ability' },
     { level: 80, name: 'Mythical Delicacy', description: 'Dishes of legend', type: 'ability' },
     { level: 99, name: 'Perfect Cuisine', description: 'The ultimate culinary achievement', type: 'ability' },
   ],
   smithing: [
     { level: 1, name: 'Iron Tools', description: 'Basic tool crafting', type: 'ability' },
     { level: 5, name: 'Bronze Dagger', description: 'Simple weapon forging', type: 'ability' },
     { level: 10, name: 'Iron Sword', description: 'Standard swords and blades', type: 'ability' },
     { level: 15, name: 'Steel Armor', description: 'Protective gear crafting', type: 'ability' },
     { level: 25, name: 'Enchanted Blade', description: 'Magic-infused weapons', type: 'ability' },
     { level: 35, name: 'Mythril Equipment', description: 'Legendary metal items', type: 'ability' },
     { level: 50, name: 'Dragon Plate', description: 'Dragonscale armor', type: 'ability' },
     { level: 65, name: 'Rune Weapons', description: 'Rune-inscribed gear', type: 'ability' },
     { level: 80, name: 'Void Steel', description: 'Otherworldly metal items', type: 'ability' },
     { level: 99, name: 'Perfect Armament', description: 'The ultimate crafted weapon', type: 'ability' },
   ],
   alchemy: [
     { level: 1, name: 'Minor Healing Potion', description: 'Basic health restoration', type: 'ability' },
     { level: 5, name: 'Speed Elixir', description: 'Movement enhancement', type: 'ability' },
     { level: 10, name: 'Fortitude Draught', description: 'Defense-boosting potion', type: 'ability' },
     { level: 20, name: 'Mana Essence', description: 'Energy replenishment', type: 'ability' },
     { level: 30, name: 'Invisibility Potion', description: 'Concealment formula', type: 'ability' },
      { level: 40, name: 'Dragon\'s Breath', description: 'Powerful alchemical concoction', type: 'ability' },
      { level: 55, name: 'Philosopher\'s Stone Extract', description: 'Legendary transmutation', type: 'ability' },
     { level: 70, name: 'Eternal Essence', description: 'Life-extending potion', type: 'ability' },
     { level: 85, name: 'Void Catalyst', description: 'Otherworldly reactions', type: 'ability' },
     { level: 99, name: 'Perfect Elixir', description: 'The ultimate alchemical creation', type: 'ability' },
   ],
 };

export const SkillsPanel: React.FC<SkillsPanelProps> = ({ socket, isOpen, onClose }) => {
  const [skills, setSkills] = useState<PlayerSkills>(createDefaultPlayerSkills());
  const [selectedSkill, setSelectedSkill] = useState<SkillName | null>(null);
  const [recentLevelUp, setRecentLevelUp] = useState<SkillName | null>(null);
  const [xpNotifications, setXpNotifications] = useState<XPNotification[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { handleHeaderMouseDown, style: dragStyle } = useDraggablePanel(60, 60, {
    storageKey: 'panel:skills',
    clampToViewport: true,
  });

  // Only show skills that are currently wired up with gameplay effects/XP
  const IMPLEMENTED_SKILLS: SkillName[] = ['gunnery', 'tactics', 'fishing', 'salvaging', 'foraging', 'mining', 'shipwright', 'cooking', 'smithing', 'alchemy', 'trading'];

  useEffect(() => {
    injectShimmerStyles();
  }, []);

  // Handle skill XP gain
  const handleSkillXP = useCallback((data: SkillXPGain) => {
    setSkills(prev => ({
      ...prev,
      [data.skill]: {
        level: data.newLevel,
        xp: data.newXp,
        totalXp: data.totalXp,
      },
    }));

    // Add XP notification
    const notification: XPNotification = {
      id: Date.now(),
      skill: data.skill,
      amount: data.xpGained,
      timestamp: Date.now(),
    };
    setXpNotifications(prev => [...prev, notification]);

    // Remove notification after animation
    setTimeout(() => {
      setXpNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 2000);
  }, []);

  // Handle level up
  const handleLevelUp = useCallback((data: SkillLevelUp) => {
    setRecentLevelUp(data.skill);
    setTimeout(() => setRecentLevelUp(null), 2000);
  }, []);

  // Set up socket listeners
  useEffect(() => {
    if (!socket) return;

    socket.on('skill:xp', handleSkillXP);
    socket.on('skill:levelUp', handleLevelUp);

    // Request initial skills data
    socket.emit('skills:request');

    // Handle skills data response
    const handleSkillsData = (data: { skills: PlayerSkills }) => {
      setSkills(data.skills);
      setIsLoading(false);
    };
    socket.on('skills:data', handleSkillsData);

    // If no response after 1 second, use defaults
    const timeout = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => {
      socket.off('skill:xp', handleSkillXP);
      socket.off('skill:levelUp', handleLevelUp);
      socket.off('skills:data', handleSkillsData);
      clearTimeout(timeout);
    };
  }, [socket, handleSkillXP, handleLevelUp]);

  if (!isOpen) return null;

  // Calculate total level (only from implemented skills for now)
  const totalLevel = IMPLEMENTED_SKILLS.reduce((sum, skill) => sum + skills[skill].level, 0);

  // Get XP progress percentage
  const getXpProgress = (skill: SkillName): number => {
    const data = skills[skill];
    if (data.level >= 100) return 100;
    const needed = xpToNextLevel(data.level);
    return (data.xp / needed) * 100;
  };

  // Render a single skill row
  const renderSkillRow = (skill: SkillName): JSX.Element => {
    const info = SKILL_INFO[skill];
    const data = skills[skill];
    const progress = getXpProgress(skill);
    const xpNeeded = xpToNextLevel(data.level);
    const isLevelingUp = recentLevelUp === skill;
    const isSelected = selectedSkill === skill;

    return (
      <div
        key={skill}
        onClick={() => setSelectedSkill(isSelected ? null : skill)}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '6px 8px',
          marginBottom: '4px',
          background: isSelected ? '#1a3a5f' : '#0d1b2a',
          border: `1px solid ${isSelected ? CATEGORY_COLORS[info.category] : '#1e3a5f'}`,
          cursor: 'pointer',
          transition: 'all 0.2s',
          animation: isLevelingUp ? 'skillLevelUp 0.5s ease-out' : 'none',
        }}
      >
        {/* Icon */}
        <div style={{
          width: '28px',
          height: '28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: CATEGORY_COLORS[info.category],
          color: '#fff',
          fontFamily: '"Sometype Mono", monospace',
          fontSize: '10px',
          fontWeight: 'bold',
          marginRight: '8px',
        }}>
          {info.icon}
        </div>

        {/* Name and XP bar */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '2px',
          }}>
            <span style={{
              fontFamily: '"Sometype Mono", monospace',
              fontSize: '11px',
              color: '#e0e0e0',
            }}>
              {info.displayName}
            </span>
            <span style={{
              fontFamily: '"Sometype Mono", monospace',
              fontSize: '11px',
              color: CATEGORY_COLORS[info.category],
              fontWeight: 'bold',
            }}>
              {data.level}
            </span>
          </div>

          {/* XP Progress bar */}
          <div style={{
            height: '4px',
            background: '#0a1628',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              width: `${progress}%`,
              background: CATEGORY_COLORS[info.category],
              transition: 'width 0.3s ease-out',
            }} />
          </div>

          {/* XP text (only when selected) */}
          {isSelected && (
            <div style={{
              fontFamily: '"Sometype Mono", monospace',
              fontSize: '9px',
              color: '#888',
              marginTop: '2px',
            }}>
              {data.xp.toLocaleString()} / {xpNeeded.toLocaleString()} XP
            </div>
          )}
        </div>

        {/* XP notification */}
        {xpNotifications.filter(n => n.skill === skill).map(n => (
          <div
            key={n.id}
            style={{
              position: 'absolute',
              right: '40px',
              color: '#4caf50',
              fontFamily: '"Sometype Mono", monospace',
              fontSize: '11px',
              fontWeight: 'bold',
              animation: 'xpFloat 2s ease-out forwards',
            }}
          >
            +{n.amount} XP
          </div>
        ))}
      </div>
    );
  };

  // Render skill category
  const renderCategory = (category: SkillCategory): JSX.Element => {
    const skillsInCategory = SKILLS_BY_CATEGORY[category].filter(skill => IMPLEMENTED_SKILLS.includes(skill));
    if (skillsInCategory.length === 0) return <React.Fragment key={category}></React.Fragment>;
    const categoryTotal = skillsInCategory.reduce((sum, skill) => sum + skills[skill].level, 0);

    return (
      <div key={category} style={{ marginBottom: '12px' }}>
        {/* Category header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '4px 8px',
          background: CATEGORY_COLORS[category],
          marginBottom: '4px',
        }}>
          <span style={{
            fontFamily: '"Sometype Mono", monospace',
            fontSize: '11px',
            fontWeight: 'bold',
            color: '#fff',
            textTransform: 'uppercase',
          }}>
            {CATEGORY_NAMES[category]}
          </span>
          <span style={{
            fontFamily: '"Sometype Mono", monospace',
            fontSize: '10px',
            color: 'rgba(255,255,255,0.8)',
          }}>
            Total: {categoryTotal}
          </span>
        </div>

        {/* Skills in category */}
        {skillsInCategory.map(skill => renderSkillRow(skill))}
      </div>
    );
  };

  // Render selected skill details
  const renderSkillDetails = (): JSX.Element | null => {
    if (!selectedSkill) return null;

    const info = SKILL_INFO[selectedSkill];
    const data = skills[selectedSkill];
    const progress = getXpProgress(selectedSkill);
    const xpNeeded = xpToNextLevel(data.level);

    return (
      <div style={{
        padding: '12px',
        background: '#0a1628',
        border: `1px solid ${CATEGORY_COLORS[info.category]}`,
        marginTop: '12px',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '8px',
        }}>
          <div style={{
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: CATEGORY_COLORS[info.category],
            color: '#fff',
            fontFamily: '"Sometype Mono", monospace',
            fontSize: '14px',
            fontWeight: 'bold',
            marginRight: '12px',
          }}>
            {info.icon}
          </div>
          <div>
            <div style={{
              fontFamily: '"Sometype Mono", monospace',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#fff',
            }}>
              {info.displayName}
            </div>
            <div style={{
              fontFamily: '"Sometype Mono", monospace',
              fontSize: '11px',
              color: CATEGORY_COLORS[info.category],
            }}>
              Level {data.level}
            </div>
          </div>
        </div>

        <p style={{
          fontFamily: '"Sometype Mono", monospace',
          fontSize: '11px',
          color: '#aaa',
          margin: '0 0 12px 0',
          lineHeight: '1.4',
        }}>
          {info.description}
        </p>

        {/* Large XP bar */}
        <div style={{
          height: '12px',
          background: '#0d1b2a',
          marginBottom: '4px',
          position: 'relative',
        }}>
          <div style={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            width: `${progress}%`,
            background: CATEGORY_COLORS[info.category],
            transition: 'width 0.3s ease-out',
          }} />
          <div style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            fontFamily: '"Sometype Mono", monospace',
            fontSize: '9px',
            color: '#fff',
            textShadow: '0 0 2px #000',
          }}>
            {Math.floor(progress)}%
          </div>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontFamily: '"Sometype Mono", monospace',
          fontSize: '10px',
          color: '#888',
        }}>
          <span>{data.xp.toLocaleString()} XP</span>
          <span>{xpNeeded.toLocaleString()} XP needed</span>
        </div>

        <div style={{
          marginTop: '8px',
          fontFamily: '"Sometype Mono", monospace',
          fontSize: '10px',
          color: '#666',
        }}>
          Total XP: {data.totalXp.toLocaleString()}
        </div>
      </div>
    );
  };

  // Render pixel art resource node icon for skill guide
  const renderResourceNodeIcon = (resourceName: string, isUnlocked: boolean): JSX.Element => {
    const opacity = isUnlocked ? 1 : 0.5;
    const size = 32;
    
    // Map resource names to visual styles (matching in-world node visuals)
    const getNodeStyle = (): { bg: string; accent: string; pattern: JSX.Element } => {
      const name = resourceName.toLowerCase();
      
      if (name.includes('debris') || name.includes('shipwreck')) {
        return {
          bg: '#8b4513',
          accent: '#654321',
          pattern: (
            <svg width={size} height={size} viewBox="0 0 32 32">
              <rect x="4" y="8" width="12" height="3" fill="#654321" opacity={opacity} />
              <rect x="16" y="14" width="10" height="3" fill="#654321" opacity={opacity} />
              <rect x="6" y="20" width="14" height="3" fill="#8b4513" opacity={opacity} />
              <rect x="24" y="10" width="4" height="4" fill="#ffffff" opacity={0.4 * opacity} />
            </svg>
          ),
        };
      }
      if (name.includes('cargo') || name.includes('crate')) {
        return {
          bg: '#daa520',
          accent: '#b8860b',
          pattern: (
            <svg width={size} height={size} viewBox="0 0 32 32">
              <rect x="6" y="8" width="20" height="16" fill="#b8860b" opacity={opacity} />
              <rect x="6" y="14" width="20" height="4" fill="#8b6914" opacity={opacity} />
              <rect x="14" y="8" width="4" height="16" fill="#8b6914" opacity={opacity} />
              <rect x="14" y="14" width="4" height="4" fill="#ffd700" opacity={0.7 * opacity} />
            </svg>
          ),
        };
      }
      if (name.includes('ruins') || name.includes('ancient')) {
        return {
          bg: '#9370db',
          accent: '#696969',
          pattern: (
            <svg width={size} height={size} viewBox="0 0 32 32">
              <rect x="4" y="12" width="8" height="14" fill="#696969" opacity={opacity} />
              <rect x="20" y="8" width="8" height="18" fill="#696969" opacity={opacity} />
              <rect x="10" y="18" width="12" height="4" fill="#808080" opacity={opacity} />
              <rect x="6" y="14" width="4" height="4" fill="#9370db" opacity={0.6 * opacity} />
            </svg>
          ),
        };
      }
      if (name.includes('quarry') || name.includes('stone')) {
        return {
          bg: '#696969',
          accent: '#4a4a4a',
          pattern: (
            <svg width={size} height={size} viewBox="0 0 32 32">
              <rect x="6" y="16" width="8" height="8" fill="#4a4a4a" opacity={opacity} />
              <rect x="18" y="14" width="6" height="6" fill="#4a4a4a" opacity={opacity} />
              <rect x="12" y="12" width="2" height="2" fill="#b87333" opacity={0.85 * opacity} />
              <rect x="22" y="22" width="2" height="2" fill="#b87333" opacity={0.85 * opacity} />
            </svg>
          ),
        };
      }
      if (name.includes('outcrop') || name.includes('mineral')) {
        return {
          bg: '#708090',
          accent: '#4a4a4a',
          pattern: (
            <svg width={size} height={size} viewBox="0 0 32 32">
              <rect x="8" y="16" width="16" height="10" fill="#4a4a4a" opacity={opacity} />
              <rect x="12" y="10" width="8" height="12" fill="#696969" opacity={opacity} />
              <rect x="14" y="12" width="4" height="6" fill="#87ceeb" opacity={0.8 * opacity} />
            </svg>
          ),
        };
      }
      if (name.includes('vent') || name.includes('volcanic')) {
        return {
          bg: '#ff4500',
          accent: '#2f4f4f',
          pattern: (
            <svg width={size} height={size} viewBox="0 0 32 32">
              <rect x="8" y="16" width="16" height="10" fill="#2f4f4f" opacity={opacity} />
              <rect x="12" y="12" width="8" height="8" fill="#ff4500" opacity={0.7 * opacity} />
              <rect x="14" y="8" width="4" height="4" fill="#ff6347" opacity={0.8 * opacity} />
              <rect x="10" y="4" width="4" height="4" fill="#dcdcdc" opacity={0.4 * opacity} />
            </svg>
          ),
        };
      }
      if (name.includes('driftwood') || name.includes('wood')) {
        return {
          bg: '#cd853f',
          accent: '#8b7355',
          pattern: (
            <svg width={size} height={size} viewBox="0 0 32 32">
              <rect x="2" y="10" width="20" height="6" fill="#8b7355" opacity={opacity} />
              <rect x="10" y="18" width="16" height="6" fill="#8b7355" opacity={opacity} />
              <rect x="6" y="4" width="12" height="4" fill="#a0522d" opacity={0.8 * opacity} />
            </svg>
          ),
        };
      }
      if (name.includes('seaweed')) {
        return {
          bg: '#2e8b57',
          accent: '#228b22',
          pattern: (
            <svg width={size} height={size} viewBox="0 0 32 32">
              <rect x="4" y="8" width="4" height="18" fill="#228b22" opacity={opacity} />
              <rect x="12" y="12" width="4" height="14" fill="#228b22" opacity={opacity} />
              <rect x="20" y="6" width="4" height="20" fill="#228b22" opacity={opacity} />
              <rect x="8" y="16" width="4" height="10" fill="#32cd32" opacity={0.7 * opacity} />
            </svg>
          ),
        };
      }
      if (name.includes('fish')) {
        return {
          bg: '#4169e1',
          accent: '#1e90ff',
          pattern: (
            <svg width={size} height={size} viewBox="0 0 32 32">
              {/* Fish body - curved shape */}
              <path d="M6,16 Q10,10 18,12 Q24,14 26,16 Q24,18 18,20 Q10,22 6,16" fill="#4a90d9" opacity={opacity} />
              {/* Tail fin */}
              <path d="M4,16 L1,11 L1,21 Z" fill="#3a7bc8" opacity={opacity} />
              {/* Top fin */}
              <path d="M14,12 L16,7 L20,12 Z" fill="#3a7bc8" opacity={opacity} />
              {/* Bottom fin */}
              <path d="M16,20 L18,24 L20,20 Z" fill="#3a7bc8" opacity={0.8 * opacity} />
              {/* Eye */}
              <circle cx="22" cy="15" r="2" fill="#ffffff" opacity={opacity} />
              <circle cx="22.5" cy="15" r="1" fill="#000000" opacity={opacity} />
              {/* Scales pattern */}
              <path d="M10,15 Q12,14 14,15 Q12,16 10,15" fill="#5ba0e9" opacity={0.6 * opacity} />
              <path d="M14,14 Q16,13 18,14 Q16,15 14,14" fill="#5ba0e9" opacity={0.6 * opacity} />
              <path d="M12,17 Q14,16 16,17 Q14,18 12,17" fill="#5ba0e9" opacity={0.6 * opacity} />
            </svg>
          ),
        };
      }
      
      // Default generic resource
      return {
        bg: '#666666',
        accent: '#444444',
        pattern: (
          <svg width={size} height={size} viewBox="0 0 32 32">
            <rect x="8" y="8" width="16" height="16" fill="#666666" opacity={opacity} />
            <rect x="12" y="12" width="8" height="8" fill="#888888" opacity={opacity} />
          </svg>
        ),
      };
    };
    
    const style = getNodeStyle();
    return style.pattern;
  };

  return (
    <OverlayPanel
      isOpen={isOpen}
      onClose={onClose}
      draggable
      storageKey="skillsPanelPosition"
      defaultPosition={{ x: 200, y: 100 }}
      closeOnBackdrop={false}
      closeOnEscape={false}
    >
      <div style={{
        position: 'fixed',
        ...dragStyle,
        width: '900px',
        maxHeight: '85vh',
        background: '#0d1117',
        border: '2px solid #21262d',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: '"Sometype Mono", monospace',
      }}>
      {/* Header */}
      <div
        onMouseDown={handleHeaderMouseDown}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: '1px solid #21262d',
          background: '#0d1117',
          cursor: 'grab',
          userSelect: 'none',
          flexShrink: 0,
        }}
      >
        <div>
          <h2 style={{
            margin: 0,
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#fff',
          }}>
            SKILLS
          </h2>
          <div style={{
            fontSize: '11px',
            color: '#888',
            marginTop: '2px',
          }}>
            Total Level: <span style={{ color: '#ffd700', fontWeight: 'bold' }}>{totalLevel}</span>
          </div>
          <div style={{
            fontSize: '10px',
            color: '#64748b',
            marginTop: '2px',
          }}>
            Fishing and Salvaging are currently active. Skill XP is saved for logged-in characters.
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: '1px solid #e74c3c',
            color: '#e74c3c',
            padding: '4px 8px',
            cursor: 'pointer',
            fontFamily: '"Sometype Mono", monospace',
            fontSize: '12px',
          }}
        >
          [X]
        </button>
      </div>

      {/* Content - Two Column Layout */}
      <div style={{
        flex: 1,
        display: 'flex',
        overflowY: 'hidden',
      }}>
        {/* Left Column - Skills List */}
        <div style={{
          flex: '0 0 35%',
          overflowY: 'auto',
          padding: '12px',
          borderRight: '1px solid #1e3a5f',
        }}>
          {isLoading ? (
            <div style={{
              textAlign: 'center',
              padding: '20px',
              color: '#888',
            }}>
              Loading skills...
            </div>
          ) : (
            <>
              {(['combat', 'gathering', 'production', 'utility'] as SkillCategory[])
                .map(cat => renderCategory(cat))}
            </>
          )}
        </div>

        {/* Right Column - Skill Details & Guide */}
        <div style={{
          flex: '1',
          overflowY: 'auto',
          padding: '12px',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {selectedSkill ? (
            <>
              {renderSkillDetails()}
              
              {/* Skill Guide Grid - Expanded */}
              {SKILL_GUIDES[selectedSkill] && (
                <div style={{
                  marginTop: '16px',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  <div style={{
                    fontFamily: '"Sometype Mono", monospace',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: CATEGORY_COLORS[SKILL_INFO[selectedSkill].category],
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                  }}>
                    Resource Progression
                  </div>
                  
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '8px',
                    flex: 1,
                    alignContent: 'start',
                  }}>
                    {SKILL_GUIDES[selectedSkill]!.map((entry, idx) => {
                      const isUnlocked = skills[selectedSkill].level >= entry.level;
                      const categoryColor = CATEGORY_COLORS[SKILL_INFO[selectedSkill].category];
                      
                      return (
                        <div
                          key={idx}
                          style={{
                            background: '#0d1117',
                            border: `1px solid ${isUnlocked ? categoryColor : '#30363d'}`,
                            padding: '8px',
                            opacity: isUnlocked ? 1 : 0.5,
                          }}
                        >
                          {/* Resource Node Visual */}
                          <div style={{
                            width: '40px',
                            height: '40px',
                            margin: '0 auto 6px auto',
                            background: '#161b22',
                            border: `1px solid ${isUnlocked ? categoryColor : '#30363d'}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                          }}>
                            {renderResourceNodeIcon(entry.name, isUnlocked)}
                          </div>

                          {/* Level Badge */}
                          <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginBottom: '4px',
                          }}>
                            <span style={{
                              background: isUnlocked ? categoryColor : '#30363d',
                              color: '#fff',
                              padding: '1px 6px',
                              fontFamily: '"Sometype Mono", monospace',
                              fontSize: '9px',
                              fontWeight: 'bold',
                            }}>
                              Lv. {entry.level}
                            </span>
                          </div>

                          {/* Resource Name */}
                          <div style={{
                            fontFamily: '"Sometype Mono", monospace',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            color: isUnlocked ? '#fff' : '#666',
                            textAlign: 'center',
                            marginBottom: '2px',
                            lineHeight: '1.2',
                          }}>
                            {entry.name}
                          </div>

                          {/* Description */}
                          <div style={{
                            fontFamily: '"Sometype Mono", monospace',
                            fontSize: '8px',
                            color: '#8b949e',
                            textAlign: 'center',
                            lineHeight: '1.2',
                          }}>
                            {entry.description}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#666',
              fontFamily: '"Sometype Mono", monospace',
              fontSize: '12px',
            }}>
              Select a skill to view details and progression
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{
        padding: '8px 12px',
        borderTop: '1px solid #1e3a5f',
        background: '#0a1628',
        fontSize: '10px',
        color: '#666',
        textAlign: 'center',
      }}>
        Select a skill to view progression and unlockable resources | ESC to close
      </div>
      </div>
    </OverlayPanel>
  );
};

export default SkillsPanel;
